/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState, useLayoutEffect, useRef, useMemo, useCallback, useEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDrag } from '@use-gesture/react';
import { Button } from '../button/Button';
import { Fab } from '../fab/Fab';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { ArrowBack } from '../private/icons/ArrowBack';
import { ArrowForward } from '../private/icons/ArrowForward';
import debounce from '../private/utils/debounce';

const PEEKVALUES = {
  none: 0,
  sm: 0.2,
  md: 0.4,
  lg: 0.6
};

const DEFAULT_BREAKPOINTS = {
  sm: {
    slidesPerView: 3,
    slidesPerGroup: 3
  },
  md: {
    slidesPerView: 4,
    slidesPerGroup: 4
  },
  lg: {
    slidesPerView: 5,
    slidesPerGroup: 5
  },
  xl: {
    slidesPerView: 6,
    slidesPerGroup: 6
  },
  '2xl': {
    slidesPerView: 7,
    slidesPerGroup: 7
  }
};

const getSlideWidth = (containerWidth, slidesPerView) => {
  return (containerWidth / slidesPerView);
};

const Filmstrip = (props) => {
  const {
    action,
    activeIndex: activeSlideProp,
    alwaysShowControls = false,
    breakpoints = DEFAULT_BREAKPOINTS,
    children = [],
    disableMargin = false,
    hideControls = false,
    gallery = false,
    loop = false,
    onChange,
    onScroll,
    onWheel,
    pagination,
    peek = 'sm',
    slidesPerView = 2,
    slidesPerGroup = 1,
    spaceBetween = 16,
    title,
    shadowPadding,
    ...other
  } = props;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [dragDown, setDragDown] = useState(false);
  const containerRef = useRef(null);
  const skipListingRef = useRef(null);
  const startScrollLeft = useRef(0);
  const peekAmount = PEEKVALUES[peek] ?? 0.2;
  const matches = useBreakpoint('sm', { current: true });

  const breakpointValues = useMemo(() => {
    const currentBreakpoint = matches.current;
    if (breakpoints[currentBreakpoint]) {
      return {
        slidesPerView: breakpoints[currentBreakpoint].slidesPerView ?? slidesPerView,
        slidesPerGroup: breakpoints[currentBreakpoint].slidesPerGroup ?? slidesPerGroup,
        spaceBetween: breakpoints[currentBreakpoint].spaceBetween ?? spaceBetween
      };
    }
    return { slidesPerView, slidesPerGroup, spaceBetween };
  }, [matches.current, slidesPerView, slidesPerGroup, spaceBetween, breakpoints]);

  const calculateCurrentSlide = useCallback(() => {
    if (!containerRef.current) return 0;

    if (breakpointValues.slidesPerView === 'auto') {
      const { scrollLeft } = containerRef.current;
      let totalWidth = 0;
      const slides = containerRef.current.querySelectorAll('.sui-carousel-slide');
      for (let i = 0; i < slides.length; i += 1) {
        totalWidth += slides[i].offsetWidth + breakpointValues.spaceBetween;
        if (totalWidth - (slides[i].offsetWidth / 2) >= scrollLeft) {
          return i;
        }
      }
      return Math.max(0, slides.length - 1);
    }

    const { offsetWidth, scrollLeft } = containerRef.current;
    const slideWidth = getSlideWidth(offsetWidth, breakpointValues.slidesPerView + peekAmount);
    const slideIndex = Math.round((scrollLeft - breakpointValues.spaceBetween) / slideWidth);

    return Math.max(0, slideIndex);
  }, [breakpointValues.slidesPerView, peekAmount, children.length]);

  const changeActiveSlide = (event, newSlide) => {
    if (currentSlide !== newSlide) {
      setCurrentSlide(newSlide);
    }
    if (onChange) {
      onChange(event, newSlide);
    }
  };

  const handleScroll = debounce((event) => {
    const newSlide = calculateCurrentSlide();
    changeActiveSlide(event, newSlide);
  }, 60);

  // Prevent dragging of images and links
  useEffect(() => {
    const preventDragStart = (event) => {
      if (event.target.tagName === 'IMG' || event.target.closest('img') || event.target.tagName === 'A' || event.target.closest('a')) {
        event.preventDefault();
        return false;
      }
    };
    const container = containerRef.current;
    if (container) {
      container.addEventListener('dragstart', preventDragStart);
    }
    return () => {
      container.removeEventListener('dragstart', preventDragStart);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        handleScroll.clear();
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  const updateSlideSize = (targetContainer) => {
    if (targetContainer.current && typeof window !== 'undefined' && breakpointValues.slidesPerView !== 'auto') {
      const { offsetWidth } = targetContainer.current;
      const { innerWidth } = window;
      const slideWidth = getSlideWidth(Math.min(offsetWidth, innerWidth), breakpointValues.slidesPerView + peekAmount);
      const slides = targetContainer.current.querySelectorAll('.sui-carousel-slide');
      slides.forEach((slide) => {
        slide.style.width = `${slideWidth > breakpointValues.spaceBetween ? slideWidth - breakpointValues.spaceBetween : 10}px`;
      });
    } else if (targetContainer.current && breakpointValues.slidesPerView === 'auto') {
      const slides = targetContainer.current.querySelectorAll('.sui-carousel-slide');
      slides.forEach((slide) => {
        slide.style.width = 'auto';
      });
    }
  };

  // Update slide size on mount and when dependencies change
  useLayoutEffect(() => {
    if (containerRef.current) {
      updateSlideSize(containerRef);
      if (typeof ResizeObserver === 'undefined') {
        console.warn('ResizeObserver is not supported in this environment. Carousel slide width wont be updated.');
        return;
      }

      const resizeObserver = new ResizeObserver((entries) => {
        updateSlideSize(containerRef);
      });
      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [slidesPerView, spaceBetween, breakpoints, matches.current, peek]);

  // Update current slide when activeSlideProp changes
  useEffect(() => {
    let newSlide = Number(activeSlideProp);
    if (typeof newSlide === 'number' && newSlide >= 0 && newSlide <= children.length - 1) {
      if (currentSlide !== newSlide) {
        changeActiveSlide(null, newSlide);
        if (containerRef.current) {
          const slideElement = containerRef.current.querySelectorAll('.sui-carousel-slide')[newSlide];
          if (slideElement) {
            const newScrollLeft = slideElement.offsetLeft;
            containerRef.current.scrollTo({
              left: newScrollLeft,
              behavior: 'smooth'
            });
          }
        }
      }
    }
  }, [activeSlideProp, children.length, breakpointValues.slidesPerView, peekAmount]);

  const nextSlide = (event, slides) => {
    if (containerRef.current) {
      const slideAmount = slides || breakpointValues.slidesPerGroup;
      let newSlideIndex;
      if (currentSlide + slideAmount > children.length - 1) {
        newSlideIndex = loop ? 0 : children.length - 1;
      } else {
        newSlideIndex = currentSlide + slideAmount;
      }
      const slideElements = containerRef.current.querySelectorAll('.sui-carousel-slide');
      const targetSlideElement = slideElements[newSlideIndex];
      if (targetSlideElement) {
        const newScrollLeft = targetSlideElement.offsetLeft;
        containerRef.current.scrollTo({
          left: Math.max(newScrollLeft, 0),
          behavior: 'smooth'
        });
        changeActiveSlide(event, newSlideIndex);
      }
    }
  };

  const prevSlide = (event, slides) => {
    if (containerRef.current) {
      const slideAmount = slides || breakpointValues.slidesPerGroup;
      let newSlideIndex;
      if (currentSlide - slideAmount < 0) {
        newSlideIndex = loop ? children.length - 1 : 0;
      } else {
        newSlideIndex = currentSlide - slideAmount;
      }
      const slideElements = containerRef.current.querySelectorAll('.sui-carousel-slide');
      const targetSlideElement = slideElements[newSlideIndex];
      if (targetSlideElement) {
        const newScrollLeft = targetSlideElement.offsetLeft;
        containerRef.current.scrollTo({
          left: Math.max(newScrollLeft, 0),
          behavior: 'smooth'
        });
        changeActiveSlide(event, newSlideIndex);
      }
    }
  };

  const outerContainerClasses = classNames('sui-group/sui-carousel sui-relative sui-w-full', {
    'sui-mb-8': !disableMargin,
  });

  const carouselClasses = classNames('sui-overflow-x-scroll sui-flex sui-h-auto', {
    'sui-carousel-scrollbar [&::-webkit-scrollbar]:sui-opacity-0': !gallery,
    'sui-pb-16 sui-pt-3': shadowPadding === '2xl',
    'sui-pb-10 sui-pt-2': shadowPadding === 'xl',
    'sui-pb-6 sui-pt-2': shadowPadding === 'lg',
    'sui-pb-3 sui-pt-2': shadowPadding === 'md',
    'sui-pb-2 sui-pt-1': shadowPadding === 'sm',
    'sui-py-1': shadowPadding === 'base',
    'sui-snap-x sui-snap-mandatory sui-scroll-smooth sui-gallery-scrollbar': gallery && !dragDown,
  });
  const slideClasses = classNames('sui-carousel-slide sui-block sui-flex-shrink-0 sui-touch-pan-x [&>*]:sui-h-full sui-touch-pan-y', {
    'sui-snap-center': gallery
  });

  const isAtBeginning = currentSlide === 0;
  const isAtEnd = currentSlide >= children.length - breakpointValues.slidesPerView;

  const leftButtonClasses = 'sui-absolute sui-top-[calc(50%-16px)] sui-flex sui-items-center sui-left-4 sui-z-tooltip';
  const rightButtonClasses = 'sui-absolute sui-top-[calc(50%-16px)] sui-flex sui-items-center sui-right-4 sui-z-tooltip';

  const leftContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete] supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100': !isAtBeginning || alwaysShowControls
  }, leftButtonClasses);
  const shadowLeftContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', leftButtonClasses);
  const rightContainerClasses = classNames('sui-hidden sui-opacity-0 sui-transition-[opacity, overlay, display] sui-ease-in-out sui-duration-[600ms] [transition-behavior:allow-discrete] supports-any-hover:group-hover/sui-carousel:starting:sui-opacity-0', {
    'supports-any-hover:group-hover/sui-carousel:sui-block supports-any-hover:group-hover/sui-carousel:sui-opacity-100': !isAtEnd || alwaysShowControls
  }, rightButtonClasses);
  const shadowRightContainerClasses = classNames('[clip-path:circle(0%)] focus-within:[clip-path:none]', rightButtonClasses);

  const header = (
    <>
      {title != null || action != null ? (
        <div className="sui-flex sui-justify-between">
          {title}
          {action ? <div className="sui-m-2">{action}</div> : null}
        </div>
      ) : null}
    </>
  );

  const handleKeyDown = (event) => {
    const nextItemKey = 'ArrowRight';
    const previousItemKey = 'ArrowLeft';
    if (event.key === 'Enter' && event.currentTarget !== document.activeElement) {
      return;
    }
    const containerElement = event.currentTarget;
    if (event.key === nextItemKey) {
      const focusedIndex = Array.from(containerElement.children).findIndex(
        (child) => child.contains(document.activeElement)
      );
      const nextIndex = focusedIndex >= 0 ? focusedIndex + 1 : currentSlide + 1;
      containerElement.children[nextIndex]?.querySelector('a[tabindex="0"],button')?.focus();
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === previousItemKey) {
      const focusedIndex = Array.from(containerElement.children).findIndex(
        (child) => child.contains(document.activeElement)
      );
      const prevIndex = focusedIndex >= 0 ? focusedIndex - 1 : currentSlide;
      containerElement.children[prevIndex]?.querySelector('a[tabindex="0"],button')?.focus();
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const dragBind = useDrag(({
    event, down, movement: [mx], first, last, velocity
  }) => {
    if (event.pointerType === 'touch') return;
    if (down !== dragDown) {
      setDragDown(down);
    }
    if (first) {
      startScrollLeft.current = containerRef.current.scrollLeft;
    }
    if (down) {
      const currentScroll = startScrollLeft.current;
      const newScrollLeft = currentScroll - mx;
      containerRef.current.scrollLeft = Math.max(0, newScrollLeft);
    }
    if (last) {
      if (velocity[0] > 0.5) {
        if (mx < 0) {
          if (gallery) {
            nextSlide(event);
          } else {
            nextSlide(event, Math.min(Math.round(velocity[0] * 1.5), breakpointValues.slidesPerGroup));
          }
        } else if (gallery) {
          prevSlide(event);
        } else {
          prevSlide(event, Math.min(Math.round(velocity[0] * 1.5), breakpointValues.slidesPerGroup));
        }
      }
      setDragDown(false);
    }
  }, {
    filterTaps: true,
    pointer: { touch: false, keys: false }
  });

  const focusNextElement = () => {
    // Elements to consider within selection
    let focusableElements = 'a:not([disabled]), button:not([disabled]), input:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])';
    if (document.activeElement) {
      let elements = Array.prototype.filter.call(
        document.querySelectorAll(focusableElements),
        function (element) {
          // Visibility check for available elements
          return (element);
        }
      );
      const filmstripContainer = containerRef.current.closest('.sui-group\\/sui-carousel');
      if (!filmstripContainer) return;
      const nextElement = elements.find((el) => {
        return !filmstripContainer.contains(el)
               // eslint-disable-next-line no-bitwise
               && (filmstripContainer.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
      });

      nextElement?.focus();
    }
  };

  const handleButton = (event) => {
    event.preventDefault();
    event.stopPropagation();
    focusNextElement();
  };

  return (
    <div className={outerContainerClasses} {...other}>
      <div className="sui-absolute sui-z-800 sui-top-2 sui-left-2 [clip-path:circle(0%)] focus-within:[clip-path:none]">
        <Button ref={skipListingRef} onClick={handleButton}>Skip Listing</Button>
      </div>
      {header}
      <div
        ref={containerRef}
        className={carouselClasses}
        style={{ gap: `${spaceBetween}px` }}
        {...dragBind()}
        onKeyDown={handleKeyDown}
        onScroll={onScroll}
        onWheel={onWheel}
      >
        {children.map((slide, index) => (
          <div
            key={`slide-${index}`}
            className={slideClasses}
            style={{ width: 'auto' }}
            role="group"
            aria-label={`${index + 1} of ${children.length}`}
          >
            {slide}
          </div>
        ))}
      </div>
      {pagination}
      {!hideControls && (
        <div className="sui-flex sui-justify-center sui-items-center">
          <div className={leftContainerClasses}>
            <Fab
              tabIndex={-1}
              disabled={isAtBeginning && !alwaysShowControls}
              onClick={prevSlide}
            >
              <ArrowBack size="small" />
            </Fab>
          </div>
          <div className={shadowLeftContainerClasses}>
            <Fab
              aria-label={isAtBeginning ? 'This is the first slide' : 'Previous slide'}
              disabled={isAtBeginning && !alwaysShowControls}
              onClick={prevSlide}
            >
              <ArrowBack size="small" />
            </Fab>
          </div>
          <div className={rightContainerClasses}>
            <Fab
              tabIndex={-1}
              disabled={isAtEnd && !alwaysShowControls}
              onClick={nextSlide}
            >
              <ArrowForward size="small" />
            </Fab>
          </div>
          <div className={shadowRightContainerClasses}>
            <Fab
              aria-label={isAtEnd ? 'This is the last slide' : 'Next slide'}
              onClick={nextSlide}
              disabled={isAtEnd && !alwaysShowControls}
            >
              <ArrowForward size="small" />
            </Fab>
          </div>
        </div>
      )}
    </div>
  );
};

Filmstrip.displayName = 'Filmstrip';

Filmstrip.propTypes = {
  /**
   * The action that goes in the upper right corner of Filmstrip
   */
  action: PropTypes.node,
  /**
   * Determines active slide on initial render. Also updates active slide when changed.
   */
  activeIndex: PropTypes.number,
  /**
   * If `true` will always show the controls.
   * @ignore
  */
  alwaysShowControls: PropTypes.bool,
  /**
   * Options per breakpoint.
   */
  breakpoints: PropTypes.shape({
    sm: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    md: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    lg: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    xl: PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    }),
    '2xl': PropTypes.shape({
      slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
      slidesPerGroup: PropTypes.number,
      spaceBetween: PropTypes.number
    })
  }),
  /**
   * The Filmstrip slides
   */
  children: PropTypes.node,
  /**
   * Disable the default margin of 32px (mb-8) under the Filmstrip.
   * When set to true, the Filmstrip will have no margin below it.
   * @default false
  */
  disableMargin: PropTypes.bool,
  /**
   * If `true` will hide the Filmstrip controls.
   * (Note that hiding the controls will impact accessibility, especially when
   * the Filmstrip has more than one slide. This prop won't prevent the scrollbar from showing up in these occasions.)
   * @ignore
  */
  hideControls: PropTypes.bool,
  /**
   * Enables Gallery mode styles and functionality. Meant only for internal use.
   * If you want a Filmstrip with Gallery-like functionality, please use the Gallery component instead,
   * because using this prop will not be enough to deliver the full Gallery experience.
   * @ignore
  */
  gallery: PropTypes.bool,
  /**
   * When true, carousel will not disable the previous and next paginator buttons.
   * If Carousel is on the first slide and the previous button is clicked, it will paginate to the last group.
   * If Carousel is on the last slide and the next button is clicked, it will paginate to the first group.
   * @ignore
   */
  loop: PropTypes.bool,
  /**
   * Logic to be called when the active slide changes
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   * Callback fired when the carousel is scrolled
   */
  onScroll: PropTypes.func,
  /**
   * @ignore
   * Callback fired when the carousel wheel is used
   */
  onWheel: PropTypes.func,
  /**
   * Slot to pass additional controls into Filmstrip.
   * @ignore
   */
  pagination: PropTypes.node,
  /**
   * Determines how much are the Filmstrip slides allowed to peek. Only works when the Carousel type is set to 'peek'
   */
  peek: PropTypes.oneOf(['sm', 'md', 'lg', 'none']),
  /**
   * Adds padding to the top and bottom of the Filmstrip based on shadow size.
   */
  shadowPadding: PropTypes.oneOf(['2xl', 'xl', 'lg', 'md', 'base']),
  /**
   * Number of slides to transition when clicking next/prev
   * @default 1
   */
  slidesPerGroup: PropTypes.number,
  /**
   * Number of slides to display at once
   * @default 2
   */
  slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  /**
   * The space between slides
   * @default 16
   */
  spaceBetween: PropTypes.number,
  /**
   * @ignore
   * Title of Filmstrip in upper left corner
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

export { Filmstrip };